import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import { getNumberFormatter } from "@/lib/utils/getNumberFormatter";
import { formatPeriod } from "@/lib/utils/formatters";
import { CellContext } from "@tanstack/react-table";
import { ReadInvoiceSpecification } from "@/types/invoiceSpecification";
import { EnumFilter } from "@/components/data-table/components/EnumFilter";
import { PROJECT_DEPENDS_ON } from "@/schemas/commonFields/projectDependsOn";
import { Row } from "@tanstack/react-table";

export function useInvoiceSpecificationsColumnOverrides() {
  const { i18n } = useTranslation();
  const numberFormatter = useMemo(
    () => getNumberFormatter(i18n.language),
    [i18n.language],
  );

  return useMemo(
    () => ({
      period: {
        id: "period",
        accessorKey: "period",
        cell: ({ getValue }: CellContext<ReadInvoiceSpecification, unknown>) =>
          formatPeriod(getValue() as string),
      },
      project_depends_on: {
        id: "project_depends_on",
        accessorKey: "project_depends_on",
        cell: ({ row }: { row: Row<ReadInvoiceSpecification> }) =>
          i18n.t(`projectDependsOn.${row.original.project_depends_on}`),
        meta: {
          filter: {
            component: EnumFilter,
            props: {
              options: PROJECT_DEPENDS_ON,
              translationKey: "projectDependsOn",
            },
          },
        },
      },
      hours: {
        id: "hours",
        accessorKey: "hours",
        cell: ({ getValue }: CellContext<ReadInvoiceSpecification, unknown>) =>
          numberFormatter(getValue() as number),
      },
      contractors_work_income: {
        id: "contractors_work_income",
        accessorKey: "contractors_work_income",
        cell: ({ getValue }: CellContext<ReadInvoiceSpecification, unknown>) =>
          numberFormatter(getValue() as number),
      },
      additions: {
        id: "additions",
        accessorKey: "additions",
        cell: ({ getValue }: CellContext<ReadInvoiceSpecification, unknown>) =>
          numberFormatter(getValue() as number),
      },
      deductions: {
        id: "deductions",
        accessorKey: "deductions",
        cell: ({ getValue }: CellContext<ReadInvoiceSpecification, unknown>) =>
          numberFormatter(getValue() as number),
      },
      costs: {
        id: "costs",
        accessorKey: "costs",
        cell: ({ getValue }: CellContext<ReadInvoiceSpecification, unknown>) =>
          numberFormatter(getValue() as number),
      },
      income: {
        id: "income",
        accessorKey: "income",
        cell: ({ getValue }: CellContext<ReadInvoiceSpecification, unknown>) =>
          numberFormatter(getValue() as number),
      },
    }),
    [i18n, numberFormatter],
  );
}
